import { ArrowDown, ArrowUp, Banknote, Check, CreditCard, Pencil, QrCode, Store, Ticket, Trash2, Wallet } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

import TaxaResumo from "../components/TaxaResumo";
import { MODULOS } from "../lib/formulario";
import { tipoDaForma } from "../lib/sugestoes";

const ONLINE = ["pix_online", "cartao_online"];

// Ícone e cor pelo tipo (a forma não guarda tipo: vem do nome/código)
const VISUAL = {
  dinheiro: { Icone: Banknote, cor: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  pix: { Icone: QrCode, cor: "bg-teal-500/15 text-teal-600 dark:text-teal-400" },
  online_pix: { Icone: QrCode, cor: "bg-teal-500/15 text-teal-600 dark:text-teal-400" },
  credito: { Icone: CreditCard, cor: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  debito: { Icone: CreditCard, cor: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  cartao: { Icone: CreditCard, cor: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  online_cartao: { Icone: CreditCard, cor: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" },
  voucher: { Icone: Ticket, cor: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  ifood: { Icone: Store, cor: "bg-red-500/15 text-red-600 dark:text-red-400" },
};
const PADRAO = { Icone: Wallet, cor: "bg-primary/10 text-primary" };

function Setas({ podeSubir, podeDescer, aoSubir, aoDescer }) {
  return (
    <div className="flex flex-col shrink-0 -my-1">
      <Button onClick={aoSubir} variant="ghost" size="sm" className="h-5 w-6 p-0 text-muted-foreground" disabled={!podeSubir} aria-label="Subir"><ArrowUp className="w-3.5 h-3.5" /></Button>
      <Button onClick={aoDescer} variant="ghost" size="sm" className="h-5 w-6 p-0 text-muted-foreground" disabled={!podeDescer} aria-label="Descer"><ArrowDown className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

/** Chip de um módulo: ligado (cor da loja, com ✓) ou desligado (tracejado). Clique troca. */
function ChipModulo({ m, ligado, aoTrocar }) {
  return (
    <button type="button" onClick={aoTrocar} data-e2e={`chip-${m.key}`} aria-pressed={ligado}
      title={`${ligado ? "Aparece" : "Não aparece"} em ${m.label} — clique para ${ligado ? "tirar" : "colocar"}`}
      className={`inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-full border text-[11px] font-medium transition-colors ${
        ligado ? "bg-primary/10 border-primary/30 text-primary hover:bg-primary/15" : "border-dashed text-muted-foreground/70 hover:text-foreground hover:border-foreground/30"
      }`}>
      <m.Icone className="w-3.5 h-3.5" />
      {m.curto}
      {ligado && <Check className="w-3 h-3" />}
    </button>
  );
}

/** Uma forma de pagamento: ícone, nome, taxa/prazo, ativa, editar, excluir e os módulos onde aparece. */
export default function LinhaForma({ pm, taxa, ativa, setas, acoes }) {
  const online = ONLINE.includes(pm.code);
  const { Icone, cor } = VISUAL[tipoDaForma(pm.name, pm.code)?.tipo] || PADRAO;
  const ligados = MODULOS.filter((m) => pm[`visible_${m.key}`]).length;
  return (
    <div className={`rounded-xl border bg-card p-3 sm:p-4 transition-colors hover:border-primary/40 ${ativa ? "" : "opacity-75"}`} data-e2e="linha-forma">
      <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
        {ativa && <Setas {...setas} />}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cor}`}><Icone className="w-5 h-5" /></div>
        <button type="button" onClick={acoes.editar} className="flex-1 min-w-[140px] text-left">
          <span className="font-semibold flex items-center gap-2 flex-wrap leading-tight">
            {pm.name}
            {online && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase bg-primary/15 text-primary">Online</span>}
          </span>
          <span className={`text-xs ${ligados ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`}>
            {ligados ? `Aparece em ${ligados} de ${MODULOS.length} módulos` : "Não aparece em nenhum módulo"}
          </span>
        </button>
        <TaxaResumo item={taxa} onClick={acoes.editar} />
        <div className="flex items-center gap-1 ml-auto">
          <label className="flex items-center gap-2 px-2 cursor-pointer" title={ativa ? "Desativar (some das telas)" : "Reativar"}>
            <span className={`text-xs font-medium hidden sm:inline ${ativa ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{ativa ? "Ativa" : "Desativada"}</span>
            <Switch checked={ativa} onCheckedChange={acoes.ativar} aria-label={ativa ? "Desativar forma" : "Reativar forma"} data-e2e="ativar-forma" />
          </label>
          <Button onClick={acoes.editar} variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Editar" data-e2e="editar-forma"><Pencil className="w-4 h-4" /></Button>
          <Button onClick={acoes.excluir} variant="ghost" size="sm" aria-label="Excluir" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5 sm:pl-[4.75rem]">
        {MODULOS.map((m) => <ChipModulo key={m.key} m={m} ligado={!!pm[`visible_${m.key}`]} aoTrocar={() => acoes.modulo(m.key)} />)}
      </div>
    </div>
  );
}
